import { useState, useEffect } from 'react'

import { Checkbox } from '@mui/material'

import { Box, Text, Switch } from '@fairfashion/design-system'

import { sdk } from '../../services/sdk'
import { formatDateToDayMounthYear } from '../../utilities/masks/dates'

import * as Styles from './provider-risk.styles'
import { ProviderRisksProps } from './provider-risks.types'

export const ProviderRisks = ({
  socialEnvironmentalRisk,
}: ProviderRisksProps) => {
  const [checkedCheckboxes, setCheckedCheckboxes] = useState<
    Record<string, boolean>
  >({})

  const [showOnlyUnchecked, setShowOnlyUnchecked] = useState(true)

  useEffect(() => {
    if (socialEnvironmentalRisk) {
      const initialStates = socialEnvironmentalRisk.reduce(
        (acc, risk) => {
          acc[risk.id] = risk.checked || false
          return acc
        },
        {} as Record<string, boolean>,
      )
      setCheckedCheckboxes(initialStates)
    }
  }, [socialEnvironmentalRisk])

  const translateTypeOfRisk = (type: string | undefined) => {
    switch (type) {
      case undefined:
        return 'indefinido'
      case 'embargo':
        return 'embargo'
      case 'slavery':
        return 'Trabalho análogo a escravidão'
      case 'registration_status':
        return 'Situação cadastral'
      case 'deforastation':
        return 'Desmatamento'
      default:
        return type
    }
  }

  const handleRiskChecked = async (riskId: string, updatedChecked: boolean) => {
    try {
      setCheckedCheckboxes((prev) => ({
        ...prev,
        [riskId]: updatedChecked,
      }))

      const response = await sdk.company.updateRiskChecked(
        riskId,
        updatedChecked,
      )

      if (!response) {
        setCheckedCheckboxes((prev) => ({
          ...prev,
          [riskId]: !updatedChecked,
        }))
      }
    } catch (error) {
      console.error(error)
      setCheckedCheckboxes((prev) => ({
        ...prev,
        [riskId]: !updatedChecked,
      }))
    }
  }

  // Filtrar riscos baseado no estado do toggle
  const filteredRisks =
    socialEnvironmentalRisk?.filter((risk) => {
      if (showOnlyUnchecked) {
        return !checkedCheckboxes[risk.id]
      }
      return true
    }) || []

  return socialEnvironmentalRisk && socialEnvironmentalRisk?.length > 0 ? (
    <Box width="100%" direction="column" gap={3}>
      <Box
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        paddingX={2}
        paddingY={2}
        style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
        }}
      >
        <Box direction="column" gap={1}>
          <Text fontWeight={500}>Filtros de visualização</Text>
          <Text color="grey_3">
            {showOnlyUnchecked
              ? 'Exibindo apenas riscos não verificados'
              : 'Exibindo todos os riscos'}
          </Text>
        </Box>
        <Box direction="row" alignItems="center" gap={2}>
          <Text>Todos os riscos</Text>
          <Switch
            checked={showOnlyUnchecked}
            onChange={() => setShowOnlyUnchecked(!showOnlyUnchecked)}
          />
          <Text>Apenas não verificados</Text>
        </Box>
      </Box>

      {filteredRisks.length > 0 ? (
        filteredRisks
          .slice()
          .sort((a, b) => {
            const dateA = a.date ? new Date(a.date).getTime() : 0
            const dateB = b.date ? new Date(b.date).getTime() : 0
            return dateB - dateA
          })
          .map((risk, index) => (
            <Box width="100%" alignItems="stretch" key={risk.id}>
              <Box flex={1} direction="column" alignItems="center">
                <Styles.Circle active={index === 0} />
                <Styles.Line />
              </Box>
              <Box width="100%" paddingX={2} direction="column">
                <Box width="100%" gap={2} alignItems="center">
                  <Text fontWeight={500}>
                    {risk.date && formatDateToDayMounthYear(risk.date)}
                  </Text>
                  <Styles.Bedge>
                    {translateTypeOfRisk(risk.typeRisk)}
                  </Styles.Bedge>
                </Box>
                <Box
                  width="100%"
                  direction="column"
                  paddingY={1}
                  marginBottom={2}
                >
                  <Text fontWeight={500}>{risk.summary}</Text>
                  <Text> {risk.description}</Text>
                  <Text color="grey_3">
                    <small>Fonte: {risk.fontRisk}</small>
                  </Text>
                </Box>
                <Box width="100%" alignItems="center">
                  <Text>Risco verificado</Text>
                  <Checkbox
                    checked={checkedCheckboxes[risk.id] || false}
                    onChange={() =>
                      handleRiskChecked(risk.id, !checkedCheckboxes[risk.id])
                    }
                  />
                </Box>
              </Box>
            </Box>
          ))
      ) : (
        <Box
          alignItems="center"
          justifyContent="center"
          width="100%"
          padding={6}
          style={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
          }}
        >
          <Text align="center" color="grey_3">
            {showOnlyUnchecked
              ? 'Todos os riscos foram verificados! 🎉'
              : 'Nenhum risco encontrado'}
          </Text>
        </Box>
      )}
    </Box>
  ) : (
    <Box alignItems="center" justifyContent="center" width="100%" padding={6}>
      <Text align="center">Nenhum risco encontrado</Text>
    </Box>
  )
}
